import type { Session } from "@supabase/supabase-js";
import type {
  BusinessReviewStatus,
  IdentityVerificationStatus,
  ProviderAgreementStatus,
  ProviderEligibilityBlockingReason,
  ProviderPayoutSetupStatus
} from "@tinkr/shared/provider-governance";
import type { ShippingRegion } from "@tinkr/shared/regions";

import { idempotencyKey, tinkrApiFetch } from "./client";
import type { ProviderRejectionCode } from "./admin-provider-operations";

export type { ProviderRejectionCode };
export type {
  BusinessReviewStatus,
  IdentityVerificationStatus,
  ProviderAgreementStatus,
  ProviderEligibilityBlockingReason,
  ProviderPayoutSetupStatus
};

export type ProviderApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn";

/**
 * ADR-P10: providers are either registered businesses or individual makers.
 * The two entity types share the dashboard and domain flows; they differ in
 * onboarding fields (company name + optional website vs optional showcase
 * URL — optional since the ADR-P10 amendment, 2026-07-24).
 */
export type ProviderEntityType = "registered_business" | "individual";

export type ProviderApplicationGateStatus =
  | "not_submitted"
  | "pending_admin_approval"
  | "awaiting_network_launch"
  | "ready_for_onboarding"
  | "provider_created"
  | "rejected";

export type ProviderApplication = {
  id: string;
  userId: string;
  entityType: ProviderEntityType;
  /** Null for individual applications (ADR-P10). */
  companyName: string | null;
  contactEmail: string;
  /** For individuals this carries the optional showcase/portfolio URL. */
  businessWebsiteUrl: string | null;
  preferredLocale: string;
  status: ProviderApplicationStatus;
  adminReviewedByUserId: string | null;
  adminReviewedAt: string | null;
  adminReason: string | null;
  networkLiveNotificationQueuedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderApplicationGate = {
  status: ProviderApplicationGateStatus;
  canStartProviderOnboarding: boolean;
};

export type ProviderSummary = {
  id: string;
  operationalStatus: "inactive" | "active" | "suspended" | "deleted";
  /** Legacy compatibility field; never use it for onboarding or eligibility. */
  businessVerificationStatus: "unverified" | "pending" | "verified" | "rejected";
  identityVerification?: { status: IdentityVerificationStatus };
  businessReview?: { status: BusinessReviewStatus };
  providerAgreement?: { status: ProviderAgreementStatus };
  payoutSetup?: { status: ProviderPayoutSetupStatus };
  eligibility?: ProviderEligibility | null;
  submittedForReviewAt: string | null;
  /** Set when the last admin review was a soft reject. Drives the
   * "Application needs changes" banner in the wizard. Optional in the
   * type so older fixtures and clients that haven't fetched it default
   * to undefined (treated the same as null by the banner gate). */
  lastAdminRejection?: ProviderLastAdminRejection | null;
};

export type ProviderEligibility = {
  canReceiveOrders: boolean;
  canReceivePayouts: boolean;
  blockingReasons: ProviderEligibilityBlockingReason[];
  orderBlockingReasons: ProviderEligibilityBlockingReason[];
  payoutBlockingReasons: ProviderEligibilityBlockingReason[];
};

export type ProviderProfile = {
  id: string;
  userId: string;
  entityType: ProviderEntityType;
  legalName: string;
  displayName: string;
  taxIdCountry: string;
  /** Launch availability of the provider's ship-from country (display state for
   * the dashboard; onboarding is decoupled from launch). off/absent =>
   * not_eligible, test => test_only, live => eligible. */
  countryAvailability: {
    countryCode: string;
    launchStatus: "off" | "test" | "live";
    orderEligibility: "not_eligible" | "test_only" | "eligible";
    /** Previously launched and now withdrawn; never "opening soon". */
    closed?: boolean;
  };
  identityVerification: {
    status: IdentityVerificationStatus;
    provider: string | null;
    verificationUrl: string | null;
    startedAt: string | null;
    updatedAt: string | null;
  };
  businessReview: {
    status: BusinessReviewStatus;
  };
  providerAgreement: {
    status: ProviderAgreementStatus;
    version: string | null;
    acceptedAt: string | null;
  };
  payoutSetup: {
    status: ProviderPayoutSetupStatus;
    provider: string | null;
  };
  eligibility: ProviderEligibility;
  equipmentTier: "hobbyist" | "prosumer" | "professional" | null;
  operationalStatus: "inactive" | "active" | "suspended" | "deleted";
  acceptingJobs: boolean;
  serviceArea: unknown | null;
  originAddress: unknown | null;
  fulfillmentMethods: string[];
  shippingCarriers: string[];
  internationalShippingEnabled: boolean;
  /** Provider opt-in for multicolor (multi-filament / AMS) jobs. Defaults to
   * true. When false, matching stops offering multicolor jobs to this
   * provider; solid-colour jobs and already accepted orders are unaffected,
   * and the printer's multi-material capability stays declared. */
  multicolorJobsEnabled: boolean;
  /** Provider's minimum order amount in their payout currency minor units, or
   * null when the matching exclusion is disabled. Compared against the
   * provider's payable at match time — a too-small job is not offered to them
   * (distinct from the rate-card price floor, which bumps the quote up). */
  minimumOrderMinor: number | null;
  /** ISO timestamp; set when the user clicks "Submit for review" in the
   * onboarding wizard. Null until then. Admins filter their queue for
   * non-null + operationalStatus !== 'active'. */
  submittedForReviewAt: string | null;
  /** Set by `reject_provider_setup` when an admin soft-rejects the last
   * submission. Cleared on resubmit + on admin approval. The lockout
   * panel renders the structured code via i18n and the optional admin
   * note verbatim with a "Note from admin (original language)" disclaimer. */
  lastAdminRejection?: ProviderLastAdminRejection | null;
  createdAt: string;
  updatedAt: string;
};

export type ProviderLastAdminRejection = {
  code: ProviderRejectionCode;
  adminNote: string | null;
  rejectedAt: string;
  rejectedByUserId: string | null;
};

export type ProviderServiceArea = {
  countryCodes: string[];
};

export type ProviderProfileResponse = {
  ok: true;
  provider: ProviderProfile;
};

export type ProviderApplicationStatusResponse = {
  ok: true;
  network: {
    providerNetworkLive: boolean;
    updatedAt: string | null;
  };
  application: ProviderApplication | null;
  provider: ProviderSummary | null;
  gate: ProviderApplicationGate;
};

/**
 * Discriminated on `entityType`. The business variant keeps `entityType`
 * optional for backward compatibility: payloads that predate the entity
 * split omit it and the API defaults them to `registered_business`.
 */
export type SubmitBusinessProviderApplicationInput = {
  entityType?: "registered_business";
  companyName: string;
  contactEmail: string;
  businessWebsiteUrl?: string;
  locale: string;
};

export type SubmitIndividualProviderApplicationInput = {
  entityType: "individual";
  contactEmail: string;
  /** Optional https link to the maker's work (portfolio, MakerWorld or
   * Printables profile, social page). Supporting evidence only since the
   * ADR-P10 amendment (2026-07-24) — portfolio photos are the required
   * proof at activation review. */
  showcaseUrl?: string;
  locale: string;
};

export type SubmitProviderApplicationInput =
  | SubmitBusinessProviderApplicationInput
  | SubmitIndividualProviderApplicationInput;

export type CreateBusinessProviderOnboardingInput = {
  entityType?: "registered_business";
  legalName: string;
  displayName: string;
  accountCountryCode: string;
  businessWebsiteUrl?: string;
};

export type CreateIndividualProviderOnboardingInput = {
  entityType: "individual";
  /** The person's legal name. PII: used for verification and payouts only,
   * never shown publicly. */
  legalName: string;
  /** Public maker name. */
  displayName: string;
  accountCountryCode: string;
  /** Optional since the ADR-P10 amendment (2026-07-24). */
  showcaseUrl?: string;
};

export type CreateProviderOnboardingInput =
  | CreateBusinessProviderOnboardingInput
  | CreateIndividualProviderOnboardingInput;

export type UpdateProviderProfileInput = {
  displayName?: string;
  serviceArea?: ProviderServiceArea | null;
  fulfillmentMethods?: Array<"pickup" | "local_delivery" | "shipped">;
  shippingCarriers?: string[];
  internationalShippingEnabled?: boolean;
  /** Opt in/out of multicolor (multi-filament / AMS) job offers. Omit the key
   * to leave the current value unchanged. */
  multicolorJobsEnabled?: boolean;
  /** Non-negative integer minor-unit floor, or null to disable the matching
   * exclusion. Omit the key to leave the current value unchanged. */
  minimumOrderMinor?: number | null;
};

export type ProviderIdentityVerificationStartResponse = {
  ok: true;
  identityVerification: ProviderProfile["identityVerification"];
};

export type SubmitProviderForReviewResponse = {
  ok: true;
  providerId: string;
  submittedForReviewAt: string;
};

export function getProviderApplicationStatus(
  session: Session
): Promise<ProviderApplicationStatusResponse> {
  return tinkrApiFetch<ProviderApplicationStatusResponse>(
    session,
    "/v1/provider/application"
  );
}

export function submitProviderApplication(
  session: Session,
  input: SubmitProviderApplicationInput
): Promise<ProviderApplicationStatusResponse> {
  return tinkrApiFetch<ProviderApplicationStatusResponse>(
    session,
    "/v1/provider/application",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-application")
      },
      body: JSON.stringify(input)
    }
  );
}

export function getProviderProfile(
  session: Session,
  signal?: AbortSignal
): Promise<ProviderProfileResponse> {
  return tinkrApiFetch<ProviderProfileResponse>(session, "/v1/provider/profile", { signal });
}

export type ProviderShippingDestinationStatus = "available" | "blocked";

// WS-SHIP: international shipping eligibility for the provider Shipping settings
// grouped-region selector. `internationalOriginEnabled` gates the whole section;
// `destinations` are the admin-enabled destinations, each available or blocked.
export type ProviderShippingEligibility = {
  internationalShippingEnabled: boolean;
  disabledReason: string | null;
  originCountryCode: string;
  originRegion: ShippingRegion | null;
  internationalOriginEnabled: boolean;
  internationalShippingOptIn: boolean;
  destinations: Array<{
    countryCode: string;
    region: ShippingRegion | null;
    status: ProviderShippingDestinationStatus;
    disabledReason: string | null;
    bubbles: Array<{slug: string; name: string; customerNotice: string}>;
  }>;
};

export function getProviderShippingEligibility(
  session: Session
): Promise<ProviderShippingEligibility> {
  return tinkrApiFetch<ProviderShippingEligibility>(
    session,
    "/v1/provider/shipping/eligibility"
  );
}

export function createProviderOnboarding(
  session: Session,
  input: CreateProviderOnboardingInput
): Promise<ProviderProfileResponse> {
  return tinkrApiFetch<ProviderProfileResponse>(session, "/v1/provider/onboarding", {
    method: "POST",
    headers: {
      "idempotency-key": idempotencyKey("provider-onboarding")
    },
    body: JSON.stringify(input)
  });
}

export function refreshProviderIdentityVerification(session: Session): Promise<{ ok: true; refreshQueued: boolean }> {
  return tinkrApiFetch(session, "/v1/provider/identity-verification/refresh", {
    method: "POST", headers: { "idempotency-key": idempotencyKey("provider-identity-refresh") },
    body: JSON.stringify({})
  });
}

export function startProviderIdentityVerification(
  session: Session,
  input: {
    returnUrl: string;
  }
): Promise<ProviderIdentityVerificationStartResponse> {
  return tinkrApiFetch<ProviderIdentityVerificationStartResponse>(
    session,
    "/v1/provider/identity-verification/start",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-identity-verification-start")
      },
      body: JSON.stringify(input)
    }
  );
}

export type ProviderWisePayoutSetupResponse = {
  ok: true;
  mode: "demo" | "live";
  /** True while the recipient-setup worker has not finished yet (the API
   * retries in the background; refresh the profile to observe completion). */
  pending: boolean;
  payoutSetup: {
    status: string;
    provider: string | null;
  };
  recipient: {
    currency: string;
    wiseRecipientAccountId: string;
  } | null;
};

/** Provider self-serve payout setup on the Wise trial rail: payout currency
 * only. The claim email is the provider's account email (resolved
 * server-side), and Wise contacts the provider directly to collect bank
 * details via a secure claim link — bank data is never entered into Tinkr. */
export function configureProviderWisePayoutSetup(
  session: Session,
  input: {
    currency: string;
  }
): Promise<ProviderWisePayoutSetupResponse> {
  return tinkrApiFetch<ProviderWisePayoutSetupResponse>(
    session,
    "/v1/provider/payout-setup/wise-email",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-wise-payout-setup")
      },
      body: JSON.stringify(input)
    }
  );
}

/** The current published provider supplier agreement, or null before legal
 * publishes one. Fetched from the API so the version can never drift from the
 * `legal_documents` row that `record_provider_agreement_accepted` validates
 * against — there is deliberately no client-side version constant. */
export type ProviderAgreementDocument = {
  version: string;
  title: string;
  summaryOfChanges: string | null;
  effectiveAt: string;
};

export type ProviderAgreementDocumentResponse = {
  published: ProviderAgreementDocument | null;
};

export function getProviderAgreement(
  session: Session
): Promise<ProviderAgreementDocumentResponse> {
  return tinkrApiFetch<ProviderAgreementDocumentResponse>(
    session,
    "/v1/provider/agreement"
  );
}

export type AcceptProviderAgreementInput = {
  /** Version string of the provider agreement being accepted. Must match the
   * current published `provider_supplier_agreement` version server-side. */
  version: string;
};

/** Passthrough of `record_provider_agreement_accepted`. `accepted` is always
 * true on success; the remaining fields echo the recorded acceptance so the
 * caller can reflect the version/date without a second fetch. */
export type AcceptProviderAgreementResponse = {
  accepted: true;
  providerId?: string;
  agreementEventId?: string;
  status?: ProviderAgreementStatus;
  agreementVersion?: string;
  acceptedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Record the provider's acceptance of the current provider supplier agreement.
 * Requires a dashboard session (the API rejects non-session actors) and an
 * Idempotency-Key, following the same convention as the sibling provider
 * mutations (identity start, Wise payout setup).
 */
export function acceptProviderAgreement(
  session: Session,
  input: AcceptProviderAgreementInput
): Promise<AcceptProviderAgreementResponse> {
  return tinkrApiFetch<AcceptProviderAgreementResponse>(
    session,
    "/v1/provider/agreement/accept",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-agreement-accept")
      },
      body: JSON.stringify({
        agreementVersion: input.version,
        confirm: true
      })
    }
  );
}

export function updateProviderProfile(
  session: Session,
  input: UpdateProviderProfileInput
): Promise<ProviderProfileResponse> {
  return tinkrApiFetch<ProviderProfileResponse>(session, "/v1/provider/profile", {
    method: "PATCH",
    headers: {
      "idempotency-key": idempotencyKey("provider-profile")
    },
    body: JSON.stringify(input)
  });
}

export function updateProviderAvailability(
  session: Session,
  acceptingJobs: boolean
): Promise<ProviderProfileResponse> {
  return tinkrApiFetch<ProviderProfileResponse>(session, "/v1/provider/availability", {
    method: "PATCH",
    headers: {
      "idempotency-key": idempotencyKey("provider-availability")
    },
    body: JSON.stringify({ acceptingJobs })
  });
}

export function submitProviderForReview(
  session: Session
): Promise<SubmitProviderForReviewResponse> {
  return tinkrApiFetch<SubmitProviderForReviewResponse>(
    session,
    "/v1/provider/review/submit",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("provider-review-submit")
      },
      body: "{}"
    }
  );
}
